import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { MAX_SELECT_HEIGHT } from 'ui/Phone/constants';
import { animate, EaseInOut } from 'utils/helpers/animate';
import useOnClickOutside from 'utils/hooks/useOnClickOutside';
import { SlotType, useSlot } from 'utils/hooks/useSlot';
import FlatList from 'ui/Phone/components/FlatList';

interface ISelect<T> {
  children: React.ReactNode;
  error: string;
  name?: string;
  disabledDefaultOnClick?: boolean;
  label?: string;
  panelClassName?: string;
  onChange?: (state: boolean) => void;
  state?: boolean;
  maxHeight?: number;
  data?: T[];
  RenderItem?: (state: T) => React.ReactNode;
  classNames?: {
    selectPanel?: string;
    selectPanelPhone?: string;
    inputLabel?: string;
    input?: string;
  };
}

export const PanelSlot: SlotType<{}> = useSlot();

const Select = <T extends {}>({
  error,
  label,
  children,
  panelClassName,
  onChange,
  disabledDefaultOnClick = false,
  state,
  name,
  maxHeight = MAX_SELECT_HEIGHT,
  RenderItem,
  data,
  classNames,
}: ISelect<T>): React.ReactElement => {
  const [openSelect, setOpenSelect] = useState<boolean>(false);
  const [mount, setMount] = useState<boolean>(false);

  const [height, setHeight] = useState<number>(0);
  const [heightMax, setHeightMax] = useState<number>(maxHeight);
  const [heightAnimation, setHeightAnimation] = useState<number | null>(null);

  const wrapper = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLUListElement>(null);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    state !== undefined && setOpenSelect(state);
  }, [state]);

  useEffect(() => {
    if (!mount) {
      setMount(true);
      return;
    }
    const height: number = content.current?.clientHeight || 0;
    if (openSelect) {
      const value: number = height > maxHeight ? maxHeight : height;
      setHeightAnimation(value);
      value !== heightMax ? setHeightMax(value) : undefined;
    } else setHeightAnimation(0);
    onChange && onChange(openSelect);
  }, [openSelect]);

  useEffect(() => {
    if (heightAnimation === null) return;
    animate({
      duration: 300,
      animation: (value: number) => setHeight(value),
      typeAnimation: EaseInOut,
      keyframes: {
        from: openSelect ? heightMax : 0,
        to: openSelect ? 0 : heightMax,
      },
    });
  }, [heightAnimation]);
  useOnClickOutside(wrapper as React.RefObject<HTMLElement>, () => setOpenSelect(false));

  return (
    <div ref={wrapper} className={cn('input', classNames?.input)}>
      {label ? (
        <label className={cn('input__label', classNames?.inputLabel)} htmlFor={name}>
          {label}
        </label>
      ) : (
        <></>
      )}

      <div className="select">
        <div
          className={cn(
            'select__panel',
            panelClassName,
            classNames?.selectPanel,
            classNames?.selectPanelPhone,
            { 'select__panel--active': openSelect },
            { 'select__panel--error': error }
          )}
          onClick={() => !disabledDefaultOnClick && setOpenSelect(!openSelect)}
        >
          <PanelSlot.Renderer childs={children} />
        </div>

        <div style={{ height }} ref={box} className="select__wrapper">
          <ul ref={content} className="select__wrapper-content">
            {RenderItem && data ? (
              <FlatList<T>
                scrollPanel={box}
                scrollView={content}
                data={data}
                renderCount={10}
                RenderItem={RenderItem}
              />
            ) : (
              children
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Select;
